import type { ColumnDef } from "@tanstack/react-table";
import { Eye } from "lucide-react";
import { Typography } from "@/components/ui/atoms/Typography";
import { dataTableSortHeader } from "@/components/ui/molecules/DataTable";
import type { JobCostSummaryResponse } from "@/services/pricing.service";
import { Button } from "@/components/ui/atoms/Button";

export interface JobCostTableRow {
  id: string;
  companyName: string;
  creatorEmail: string;
  jobTitle: string;
  totalPrice: number;
}

export function mapJobCostSummaryToRow(summary: JobCostSummaryResponse): JobCostTableRow {
  return {
    id: summary.job_id,
    companyName: summary.company_name || "N/A",
    creatorEmail: summary.creator_email || "N/A",
    jobTitle: summary.job_title,
    totalPrice: summary.total_price,
  };
}

export interface JobCostTableColumnsOptions {
  formatCurrency: (amount: number) => string;
  onViewDetails: (jobId: string) => void;
}

export function createJobCostTableColumns({
  formatCurrency,
  onViewDetails,
}: JobCostTableColumnsOptions): ColumnDef<JobCostTableRow>[] {
  return [
    {
      accessorKey: "companyName",
      meta: { label: "Company" },
      header: dataTableSortHeader("Company"),
      size: 220,
      minSize: 140,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <Typography variant="body-sm" weight="semibold" color="primary">
            {row.original.companyName}
          </Typography>
          <Typography variant="body-sm" weight="medium" color="muted">
            {row.original.creatorEmail}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: "jobTitle",
      meta: { label: "Job title" },
      header: dataTableSortHeader("Job title"),
      size: 200,
      minSize: 120,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="secondary">
          {row.original.jobTitle}
        </Typography>
      ),
    },
    {
      accessorKey: "totalPrice",
      meta: { label: "Total cost" },
      header: dataTableSortHeader("Total cost"),
      size: 140,
      minSize: 100,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="primary">
          {formatCurrency(row.original.totalPrice)}
        </Typography>
      ),
    },
    {
      id: "actions",
      meta: { label: "Actions" },
      header: () => (
        <span className="block text-center font-heading text-xs font-bold uppercase tracking-wider text-dark-invirtus">
          Actions
        </span>
      ),
      size: 120,
      minSize: 100,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Button type="contained"
            intent="info"
            size="xs"
            label="Details"
            title="View cost details"
            aria-label="View cost details"
            id={`view-cost-${row.original.id}`}
            onClick={() => onViewDetails(row.original.id)}
            icon={<Eye />}
          />
        </div>
      ),
    },
  ];
}
